import { skipToken } from '@reduxjs/toolkit/query';
import { DialogWithForm } from '@strata/react-dialog';
import { DynamicInputFormik } from '@strata/react-dynamic-input';
import {
    isPerformanceValueCaseAutomatic,
    isPerformanceValueCaseManual,
} from '@strata/react-performance';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import * as Yup from 'yup';

import {
    ConversationActionMap,
    ConversationActions,
    FactDataTypes,
    getNewFactsReducer,
    isFactValid,
    PerformanceTypes,
    reduceCurrentFacts,
} from 'services/userSupportHelpers';
import {
    useGetConversationTopicsQuery,
    useGetTopicFactsQuery,
    useLazyGetTopicFactsQuery,
} from 'store/api/userSupportChatApi';

export const ConversationDialog = ({
    action = undefined,
    onCreate,
    onChange,
    onCancel,
    performanceEntries = undefined,
    currentFacts = undefined,
    currentTopic = undefined,
}) => {
    const { data: allTopics } = useGetConversationTopicsQuery(action ? undefined : skipToken);
    const topics = useMemo(
        () =>
            (performanceEntries ?? []).length < 1
                ? allTopics?.filter((topic) => topic.secondaryData !== 'Performance Issue')
                : allTopics,
        [allTopics, performanceEntries],
    );

    const changingTopic = action === ConversationActions.CHANGE ? currentTopic : undefined;
    const { data: currentTopicFacts } = useGetTopicFactsQuery(changingTopic ?? skipToken);
    const [getTopicFacts] = useLazyGetTopicFactsQuery();

    const initialFacts = useMemo(
        () =>
            currentTopicFacts && currentFacts
                ? currentFacts.reduce(
                      reduceCurrentFacts,
                      currentTopicFacts.reduce(...getNewFactsReducer()),
                  )
                : undefined,
        [currentFacts, currentTopicFacts],
    );

    const details = {
        [FactDataTypes.PERFORMANCE_REF]: (performanceEntries ?? []).reduce((prev, curr) => {
            const anyManualTasks = isPerformanceValueCaseManual(curr.tasks);
            const anyAutomaticTasks = isPerformanceValueCaseAutomatic(curr.tasks);
            let type;
            if (anyManualTasks && anyAutomaticTasks) {
                type = PerformanceTypes.HYBRID;
            } else if (anyManualTasks) {
                type = PerformanceTypes.MANUAL;
            } else if (anyAutomaticTasks) {
                type = PerformanceTypes.AUTOMATIC;
            }
            return {
                ...prev,
                [curr.performance_requirement_id]: {
                    name: curr.value_case_name,
                    type,
                },
            };
        }, {}),
    };

    return (
        <Formik
            initialValues={{
                details,
                ...(action === ConversationActions.CHANGE && currentTopic && topics
                    ? {
                          topic: topics.find((topic) => topic.value === currentTopic),
                          facts: initialFacts,
                      }
                    : {
                          topic: undefined,
                      }),
            }}
            onSubmit={async (values, { resetForm }) => {
                if (action === ConversationActions.CREATE) {
                    await onCreate(values);
                } else if (action === ConversationActions.CHANGE) {
                    await onChange(values);
                }
                resetForm();
            }}
            validationSchema={Yup.object({
                topic: Yup.object({
                    name: Yup.string(),
                    value: Yup.string(),
                }).required(),
                facts: Yup.lazy((facts) =>
                    Yup.object({
                        ...Object.keys(facts ?? {}).reduce((prev, curr) => {
                            const obj = facts?.[curr];
                            if (!isFactValid(obj, facts, details)) {
                                return prev;
                            }
                            // If changing conversation details, ignore message template.
                            if (
                                action === ConversationActions.CHANGE &&
                                obj?.data_type === FactDataTypes.MESSAGE_TEMPLATE
                            ) {
                                return prev;
                            }
                            if (obj?.is_iterable === true) {
                                return {
                                    ...prev,
                                    [curr]: Yup.object({
                                        datum_value: Yup.array(
                                            DynamicInputFormik.getYupType({
                                                fieldType: obj?.shape?.type,
                                                isRequired: obj?.is_required,
                                                customComponents: {},
                                            }),
                                        ),
                                    }),
                                };
                            }
                            return {
                                ...prev,
                                [curr]: Yup.object({
                                    datum_value: DynamicInputFormik.getYupType({
                                        fieldType: obj?.shape?.type,
                                        isRequired: obj?.is_required,
                                        customComponents: {},
                                    }),
                                }),
                            };
                        }, {}),
                    }),
                ),
            })}
            enableReinitialize
        >
            {(formik) => (
                <DialogWithForm
                    isOpen={!!action}
                    dialogTitle={ConversationActionMap?.[action]?.title ?? ''}
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    onSecondaryButtonClick={() => {
                        onCancel();
                        formik.resetForm();
                    }}
                    primaryButtonLabel={ConversationActionMap?.[action]?.actionLabel ?? ''}
                    secondaryButtonLabel="Cancel"
                    width={650}
                    dialogContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="topic"
                                label="What can we help you with?"
                                required
                                values={topics}
                                onChange={async (newValue) => {
                                    await formik.setFieldValue('topic', newValue);
                                    // Topic schemas don't change, so a cached result is fine
                                    const topicFacts = await getTopicFacts(
                                        newValue.value,
                                        true,
                                    ).unwrap();
                                    const mappedFacts = topicFacts.reduce(
                                        ...getNewFactsReducer(true),
                                    );
                                    let allFacts = mappedFacts;
                                    if (
                                        action === ConversationActions.CHANGE &&
                                        currentFacts &&
                                        currentTopic &&
                                        newValue.value === currentTopic
                                    ) {
                                        allFacts = currentFacts.reduce(
                                            reduceCurrentFacts,
                                            mappedFacts,
                                        );
                                    }
                                    await formik.setFieldValue('facts', allFacts);
                                }}
                            />
                            {Object.entries(formik.values?.facts ?? {})
                                .sort(([, a], [, b]) => a.order_index - b.order_index)
                                .map(([key, fact]) => {
                                    let component = {
                                        ...fact?.shape,
                                        required: fact?.is_required,
                                        label: fact?.label,
                                        optionsDict: fact?.shape?.options_dict,
                                        showOther: fact?.shape?.show_other,
                                    };
                                    // If changing conversation details, do not allow editing message template
                                    if (
                                        action === ConversationActions.CHANGE &&
                                        fact.data_type === FactDataTypes.MESSAGE_TEMPLATE
                                    ) {
                                        component = {
                                            ...component,
                                            disabled: true,
                                        };
                                    }
                                    if (fact.data_type === FactDataTypes.PERFORMANCE_REF) {
                                        component = {
                                            ...component,
                                            optionsDict: (performanceEntries ?? []).reduce(
                                                (prev, curr) => ({
                                                    ...prev,
                                                    [curr.performance_requirement_id]:
                                                        curr.value_case_name,
                                                }),
                                                {},
                                            ),
                                        };
                                    }
                                    if (fact.is_iterable) {
                                        component = {
                                            type: DynamicInputFormik.defaultFieldTypes.FIELD_LIST,
                                            label: '',
                                            addItemLabel: fact?.add_item_label,
                                            itemComponent: component,
                                        };
                                    }
                                    if (
                                        !isFactValid(
                                            fact,
                                            formik.values?.facts,
                                            formik.values?.details,
                                        )
                                    ) {
                                        return null;
                                    }
                                    return (
                                        <DynamicInputFormik
                                            key={key}
                                            component={component}
                                            id={`facts.${key}.datum_value`}
                                        />
                                    );
                                })}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

ConversationDialog.propTypes = {
    action: PropTypes.oneOf(Object.values(ConversationActions)),
    onCreate: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    performanceEntries: PropTypes.arrayOf(PropTypes.shape({})),
    currentFacts: PropTypes.arrayOf(PropTypes.shape({})),
    currentTopic: PropTypes.string,
};
